import { DragDropContext } from '@hello-pangea/dnd';
import {
  CheckCircle,
  Cancel,
  FilterList,
  Groups,
  Inventory2,
  People,
  Search,
  Sell,
  TrendingUp,
  ViewColumn,
  VisibilityOff,
} from '@mui/icons-material';
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { salesReps } from '@/constants';
import React, { useMemo, useState } from 'react';
import { LeadStatus, Lead } from '@otw/models/leads/lead';
import { useInventoryLeads } from 'hooks/admin/useInventoryLeads';
import { useInventoryLeadColumns } from 'hooks/admin/useInventoryLeadColumns';
import { useContactCompanies } from 'hooks/contacts/useContactCompanies';
import { getUserDisplayName, useInventoryAssignableUsers } from 'hooks/useUsers';
import type { OutOfScopeLeadMatch } from '../../outOfScopeLeadMatch';
import {
  buildInventoryLeadFilterOptions,
  countInventoryActiveFilters,
  type InventoryLeadActiveFilters,
} from '../../util/inventoryLeadFilters';
import {
  INVENTORY_BORDER_COLORS,
  INVENTORY_STATUS_NAMES,
} from '../../util/inventoryStatusHelpers';
import { getStatusLabel } from '../../util';
import { INVENTORY_LEAD_DEFAULT_STATUSES } from '@otw/models/leads/inventoryLead';
import { LeadList } from '../cards';
import { LeadDetailDialog, LeadFormDialog } from '../dialogs';
import InventoryLeadsBoard, {
  type InventoryBoardStats,
} from '../InventoryLeadsBoard';
import { CrmPageActions } from '../CrmPageChrome';
import { MobileRecordCard, type MobileRecordCardChip } from 'components/MobileRecordCard';
import { MobileRecordList } from 'components/MobileRecordList';
import { leadStatusTone } from 'components/StatusChip';

/** Unique truck type/size labels a lead wants, for the card chip row. */
const collectLeadWants = (lead: Lead): MobileRecordCardChip[] => {
  const types = lead.truckRequest?.types ?? [];
  const values = new Set<string>();
  for (const entry of types) {
    for (const field of ['type', 'size'] as const) {
      const value = entry[field];
      if (Array.isArray(value)) value.forEach((v) => v && v !== 'Any' && values.add(String(v)));
      else if (value && value !== 'Any') values.add(String(value));
    }
  }
  return [...values].map((label) => ({ label, tone: 'neutral' as const }));
};

const INVENTORY_MONTHS = [
  { value: '0', label: 'January' },
  { value: '1', label: 'February' },
  { value: '2', label: 'March' },
  { value: '3', label: 'April' },
  { value: '4', label: 'May' },
  { value: '5', label: 'June' },
  { value: '6', label: 'July' },
  { value: '7', label: 'August' },
  { value: '8', label: 'September' },
  { value: '9', label: 'October' },
  { value: '10', label: 'November' },
  { value: '11', label: 'December' },
];

const INVENTORY_YEARS = Array.from({ length: 6 }, (_, i) =>
  (new Date().getFullYear() - i).toString(),
);

const PRIORITY_LABEL: Record<'low' | 'medium' | 'high', string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

function buildOutOfScopeSecondaryLine(lead: OutOfScopeLeadMatch): string {
  return [
    lead.leadNumber?.trim() ? `Lead ${lead.leadNumber.trim()}` : null,
    `Status: ${getStatusLabel(lead.status)}`,
    lead.email?.trim(),
    lead.phone?.trim(),
  ]
    .filter(Boolean)
    .join(' · ');
}

function buildOutOfScopeMetaLine(lead: OutOfScopeLeadMatch): string | null {
  const parts: string[] = [];
  const loc = lead.location?.trim();
  if (loc) parts.push(loc);
  const src = lead.source?.trim();
  if (src) parts.push(`Source: ${src}`);
  const tf = lead.timeFrame?.trim();
  if (tf) parts.push(tf);
  const vi = lead.vehicleInterest?.trim();
  if (vi) parts.push(vi);
  const fs = lead.financingStatus?.trim();
  if (fs) parts.push(`Financing: ${fs}`);
  if (lead.priority) parts.push(`${PRIORITY_LABEL[lead.priority]} priority`);
  return parts.length ? parts.join(' · ') : null;
}

interface LeadsTabProps {
  isActive: boolean;
  vm: any;
}

type LeadPipeline = 'sales' | 'inventory';

const LeadsTab: React.FC<LeadsTabProps> = ({ isActive, vm }) => {
  const theme = useTheme();
  const [pipeline, setPipeline] = useState<LeadPipeline>('sales');
  const [inventoryStats, setInventoryStats] = useState<InventoryBoardStats>({
    total: 0,
    active: 0,
    won: 0,
    lost: 0,
  });
  const [inventoryColumnSignal, setInventoryColumnSignal] = useState(0);

  const { inventoryLeads = [] } = useInventoryLeads();
  const { companies: contactCompanies = [] } = useContactCompanies();
  const { users: inventoryReps = [] } = useInventoryAssignableUsers();
  const { customColumns: inventoryCustomColumns = [] } = useInventoryLeadColumns();

  const [inventoryActiveFilters, setInventoryActiveFilters] =
    useState<InventoryLeadActiveFilters>({});
  const [inventoryHiddenColumnIds, setInventoryHiddenColumnIds] = useState<string[]>([]);
  const [isInventoryFilterDialogOpen, setIsInventoryFilterDialogOpen] = useState(false);
  const [invFilterCompanyId, setInvFilterCompanyId] = useState('');
  const [invFilterAssignedRep, setInvFilterAssignedRep] = useState('');
  const [invFilterYear, setInvFilterYear] = useState('');
  const [invFilterMonth, setInvFilterMonth] = useState('');
  const [invFilterTruckType, setInvFilterTruckType] = useState('');
  const [invFilterTruckMake, setInvFilterTruckMake] = useState('');
  const [invFilterTruckModel, setInvFilterTruckModel] = useState('');
  const [invFilterTruckSize, setInvFilterTruckSize] = useState('');
  const [invFilterTruckYear, setInvFilterTruckYear] = useState('');
  const [invFilterLostReasonId, setInvFilterLostReasonId] = useState('');

  const companiesById = useMemo(
    () => new Map(contactCompanies.map((company) => [company.id, company])),
    [contactCompanies],
  );

  const inventoryFilterOptions = useMemo(
    () =>
      buildInventoryLeadFilterOptions(
        inventoryLeads,
        (companyId) => companiesById.get(companyId)?.name || companyId,
      ),
    [inventoryLeads, companiesById],
  );

  const inventoryColumnOptions = useMemo(() => {
    const defaults = INVENTORY_LEAD_DEFAULT_STATUSES.map((status) => ({
      id: status,
      name: INVENTORY_STATUS_NAMES[status],
      color: INVENTORY_BORDER_COLORS[status],
    }));
    const customs = [...inventoryCustomColumns]
      .sort((a, b) => a.position - b.position)
      .map((column) => ({
        id: column.id,
        name: column.name,
        color: column.color,
      }));
    return [...defaults, ...customs];
  }, [inventoryCustomColumns]);

  const toggleInventoryColumnVisibility = (columnId: string) => {
    setInventoryHiddenColumnIds((prev) =>
      prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId],
    );
  };

  const getInventoryRepName = (repId: string) => {
    const user = inventoryReps.find((rep) => String(rep.id) === String(repId));
    return user ? getUserDisplayName(user) : repId;
  };

  const syncInventoryFilterDraft = (filters: InventoryLeadActiveFilters) => {
    setInvFilterCompanyId(filters.companyId || '');
    setInvFilterAssignedRep(filters.assignedRepId || '');
    setInvFilterYear(filters.year || '');
    setInvFilterMonth(filters.month || '');
    setInvFilterTruckType(filters.truckType || '');
    setInvFilterTruckMake(filters.truckMake || '');
    setInvFilterTruckModel(filters.truckModel || '');
    setInvFilterTruckSize(filters.truckSize || '');
    setInvFilterTruckYear(filters.truckYear || '');
    setInvFilterLostReasonId(filters.lostReasonId || '');
  };

  const openInventoryFilterDialog = () => {
    syncInventoryFilterDraft(inventoryActiveFilters);
    setIsInventoryFilterDialogOpen(true);
  };

  const applyInventoryFilters = () => {
    const next: InventoryLeadActiveFilters = {};
    if (invFilterCompanyId) next.companyId = invFilterCompanyId;
    if (invFilterAssignedRep) next.assignedRepId = invFilterAssignedRep;
    if (invFilterYear) next.year = invFilterYear;
    if (invFilterMonth) next.month = invFilterMonth;
    if (invFilterTruckType) next.truckType = invFilterTruckType;
    if (invFilterTruckMake) next.truckMake = invFilterTruckMake;
    if (invFilterTruckModel) next.truckModel = invFilterTruckModel;
    if (invFilterTruckSize) next.truckSize = invFilterTruckSize;
    if (invFilterTruckYear) next.truckYear = invFilterTruckYear;
    if (invFilterLostReasonId) next.lostReasonId = invFilterLostReasonId;
    setInventoryActiveFilters(next);
    setIsInventoryFilterDialogOpen(false);
  };

  const clearInventoryFilters = () => {
    setInventoryActiveFilters({});
    syncInventoryFilterDraft({});
  };

  const removeInventoryFilter = (key: keyof InventoryLeadActiveFilters) => {
    setInventoryActiveFilters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const {
    searchInputValue,
    handleSearchInputChange,
    handleSearchKeyDown,
    handleSearchClick,
    showHiddenLeads,
    setShowHiddenLeads,
    showOtherSalesReps,
    setShowOtherSalesReps,
    hasViewAllPermission,
    handleOpenFilterDialog,
    handleOpenColumnDialog,
    activeFilters,
    searchQuery,
    clearSearch,
    removeFilter,
    months,
    getRepNameById,
    clearFilters,
    outOfScopeLeadMatches,
    onDragEnd,
    isMobile,
    visibleColumnOrder,
    state,
    defaultColumns,
    handleLeadSelect,
    handleViewDetails,
    handleViewSuggestedTrucks,
    handleDeleteColumn,
    handleDeleteLead,
    isDialogOpen,
    handleCloseDialog,
    selectedLead,
    isCreatingLead,
    initialClientForLead,
    isDetailDialogOpen,
    handleCloseDetailDialog,
    handleEditFromDetails,
    isColumnDialogOpen,
    setIsColumnDialogOpen,
    newColumnTitle,
    setNewColumnTitle,
    newColumnPosition,
    setNewColumnPosition,
    handleCreateColumn,
    isDeleteColumnDialogOpen,
    setIsDeleteColumnDialogOpen,
    columnToDelete,
    targetColumnForLeads,
    setTargetColumnForLeads,
    confirmDeleteColumn,
    isFilterDialogOpen,
    setIsFilterDialogOpen,
    hiddenColumnIds,
    toggleColumnVisibility,
    filterCompany,
    setFilterCompany,
    availableCompanies,
    filterAssignedRep,
    setFilterAssignedRep,
    filterYear,
    handleYearChange,
    years,
    filterMonth,
    setFilterMonth,
    filterTruckType,
    setFilterTruckType,
    availableTruckTypes,
    filterTruckMake,
    setFilterTruckMake,
    availableTruckMakes,
    filterTruckModel,
    setFilterTruckModel,
    availableTruckModels,
    filterTruckSize,
    setFilterTruckSize,
    availableTruckSizes,
    filterTruckYear,
    setFilterTruckYear,
    availableTruckYears,
    applyFilters,
    isDeleteLeadDialogOpen,
    setIsDeleteLeadDialogOpen,
    leadToDelete,
    confirmDeleteLead,
    isMissingDropoffDialogOpen,
    setIsMissingDropoffDialogOpen,
  } = vm;

  const salesStats = useMemo(() => {
    const allColumns = state?.columnOrder || [];
    let total = 0;
    let active = 0;
    let won = 0;
    let lost = 0;
    allColumns.forEach((columnId: string) => {
      const column = state.columns?.[columnId];
      if (!column) return;
      const count = column.leads?.length || 0;
      total += count;
      if (columnId === 'closed') {
        won += count;
      } else if (columnId === 'lost') {
        lost += count;
      } else {
        active += count;
      }
    });
    return { total, active, won, lost };
  }, [state]);

  const stats = pipeline === 'inventory' ? inventoryStats : salesStats;

  /** Grouped phone list — status sub-headers, no drag columns. */
  const mobileSalesGroups = useMemo<Array<{ columnId: string; name: string; leads: Lead[] }>>(() => {
    if (!state?.columns || !visibleColumnOrder) return []
    return (visibleColumnOrder as string[])
      .map((columnId) => {
        const column = state.columns[columnId]
        return { columnId, name: (column?.name as string) || columnId, leads: (column?.leads as Lead[]) || [] }
      })
      .filter((group) => group.leads.length > 0)
  }, [state, visibleColumnOrder])

  const mobileSalesTotal = useMemo(
    () => mobileSalesGroups.reduce((sum, group) => sum + group.leads.length, 0),
    [mobileSalesGroups],
  )

  const handleInventoryStatsChange = useMemo(
    () => (next: InventoryBoardStats) => setInventoryStats(next),
    [],
  );

  if (!isActive) return null;

  const salesActiveFilterCount =
    Object.keys(activeFilters || {}).length + (searchQuery ? 1 : 0);
  const inventoryActiveFilterCount =
    countInventoryActiveFilters(inventoryActiveFilters) + (searchQuery ? 1 : 0);
  const activeFilterCount =
    pipeline === 'inventory' ? inventoryActiveFilterCount : salesActiveFilterCount;

  const handleOpenNewColumn = () => {
    if (pipeline === 'inventory') {
      setInventoryColumnSignal((n) => n + 1);
    } else {
      handleOpenColumnDialog();
    }
  };

  const kpi = (
    icon: React.ReactNode,
    label: string,
    value: React.ReactNode,
    accent: string,
  ) => (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        borderRadius: 2,
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(accent, 0.12)} 0%, ${alpha(accent, 0.02)} 100%)`,
        borderColor: alpha(accent, 0.25),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Avatar
          variant="rounded"
          sx={{
            bgcolor: alpha(accent, 0.18),
            color: accent,
            width: 32,
            height: 32,
            '& .MuiSvgIcon-root': { fontSize: 18 },
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.1, fontSize: '0.65rem' }}
          >
            {label}
          </Typography>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.15, fontSize: '1rem' }} noWrap>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  return (
    <Box sx={{ pb: 2 }}>
      <CrmPageActions active={isActive}>
        <ToggleButtonGroup
          value={pipeline}
          exclusive
          size="small"
          onChange={(_, next: LeadPipeline | null) => {
            if (next) setPipeline(next);
          }}
          sx={{
            flexWrap: 'wrap',
            '& .MuiToggleButton-root': {
              px: { xs: 1.5, sm: 2.5 },
              fontWeight: 600,
              textTransform: 'none',
            },
          }}
        >
          <ToggleButton value="sales">
            <Sell fontSize="small" sx={{ mr: 1 }} />
            Sales
          </ToggleButton>
          <ToggleButton value="inventory">
            <Inventory2 fontSize="small" sx={{ mr: 1 }} />
            Inventory
          </ToggleButton>
        </ToggleButtonGroup>
      </CrmPageActions>

      {/* KPIs */}
      <Grid container spacing={1} sx={{ mb: 1.5 }}>
        <Grid item xs={6} md={3}>
          {kpi(<Groups />, 'Total Leads', stats.total, theme.palette.primary.main)}
        </Grid>
        <Grid item xs={6} md={3}>
          {kpi(<TrendingUp />, 'In Pipeline', stats.active, theme.palette.info.main)}
        </Grid>
        <Grid item xs={6} md={3}>
          {kpi(
            <CheckCircle />,
            pipeline === 'inventory' ? 'Won' : 'Closed',
            stats.won,
            theme.palette.success.main,
          )}
        </Grid>
        <Grid item xs={6} md={3}>
          {kpi(<Cancel />, 'Lost', stats.lost, theme.palette.error.main)}
        </Grid>
      </Grid>

      {/* Search + filters bar */}
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          mb: 1.5,
          borderRadius: 2,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 1,
          alignItems: { md: 'center' },
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search leads by lead #, name, company, or rep..."
          value={searchInputValue}
          onChange={handleSearchInputChange}
          onKeyDown={handleSearchKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInputValue ? (
              <InputAdornment position="end">
                <Button size="small" onClick={handleSearchClick} sx={{ minWidth: 0, px: 1 }}>
                  Search
                </Button>
              </InputAdornment>
            ) : undefined,
          }}
          sx={{ flexGrow: 1, minWidth: 0 }}
        />

        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

        <Tooltip
          title={
            pipeline === 'inventory'
              ? 'Include won/lost leads'
              : 'Include closed/lost leads'
          }
        >
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showHiddenLeads}
                onChange={(event) => setShowHiddenLeads(event.target.checked)}
                icon={<VisibilityOff fontSize="small" />}
                checkedIcon={<VisibilityOff fontSize="small" />}
              />
            }
            label={pipeline === 'inventory' ? 'Show won/lost' : 'Show closed/lost'}
            sx={{ whiteSpace: 'nowrap', mr: 0 }}
          />
        </Tooltip>

        {hasViewAllPermission && pipeline === 'sales' && (
          <Tooltip title="Include leads assigned to other sales reps">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showOtherSalesReps}
                  onChange={(event) => setShowOtherSalesReps(event.target.checked)}
                  icon={<People fontSize="small" />}
                  checkedIcon={<People fontSize="small" />}
                />
              }
              label="Show other sales reps"
              sx={{ whiteSpace: 'nowrap', mr: 0 }}
            />
          </Tooltip>
        )}

        <Button
          variant={activeFilterCount > 0 ? 'contained' : 'outlined'}
          color={activeFilterCount > 0 ? 'primary' : 'inherit'}
          startIcon={<FilterList />}
          onClick={
            pipeline === 'inventory' ? openInventoryFilterDialog : handleOpenFilterDialog
          }
          sx={{ whiteSpace: 'nowrap', px: 4 }}
        >
          Filters
          {activeFilterCount > 0 && (
            <Chip
              size="small"
              label={activeFilterCount}
              sx={{
                ml: 1,
                height: 20,
                fontWeight: 700,
                bgcolor: alpha(theme.palette.common.white, 0.25),
                color: 'common.white',
              }}
            />
          )}
        </Button>

        <Button
          variant="outlined"
          color="inherit"
          startIcon={<ViewColumn />}
          onClick={handleOpenNewColumn}
          sx={{ whiteSpace: 'nowrap', px: 4 }}
        >
          New column
        </Button>
      </Paper>

      {/* Active filter chips */}
      {activeFilterCount > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2,
            alignItems: 'center',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, mr: 0.5 }}
          >
            Active filters:
          </Typography>
          {searchQuery && (
            <Chip
              size="small"
              label={`Search: ${searchQuery}`}
              onDelete={clearSearch}
              color="primary"
              variant="outlined"
            />
          )}
          {pipeline === 'inventory' ? (
            <>
              {inventoryActiveFilters.companyId && (
                <Chip
                  size="small"
                  label={`Company: ${companiesById.get(inventoryActiveFilters.companyId)?.name || inventoryActiveFilters.companyId}`}
                  onDelete={() => removeInventoryFilter('companyId')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {inventoryActiveFilters.year && (
                <Chip
                  size="small"
                  label={`Year: ${inventoryActiveFilters.year}`}
                  onDelete={() => removeInventoryFilter('year')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {inventoryActiveFilters.month && (
                <Chip
                  size="small"
                  label={`Month: ${INVENTORY_MONTHS.find((m) => m.value === inventoryActiveFilters.month)?.label}`}
                  onDelete={() => removeInventoryFilter('month')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {inventoryActiveFilters.assignedRepId && (
                <Chip
                  size="small"
                  label={`Inventory rep: ${getInventoryRepName(inventoryActiveFilters.assignedRepId)}`}
                  onDelete={() => removeInventoryFilter('assignedRepId')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {inventoryActiveFilters.truckType && (
                <Chip
                  size="small"
                  label={`Type: ${inventoryActiveFilters.truckType}`}
                  onDelete={() => removeInventoryFilter('truckType')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {inventoryActiveFilters.truckMake && (
                <Chip
                  size="small"
                  label={`Make: ${inventoryActiveFilters.truckMake}`}
                  onDelete={() => removeInventoryFilter('truckMake')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {inventoryActiveFilters.truckModel && (
                <Chip
                  size="small"
                  label={`Model: ${inventoryActiveFilters.truckModel}`}
                  onDelete={() => removeInventoryFilter('truckModel')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {inventoryActiveFilters.truckSize && (
                <Chip
                  size="small"
                  label={`Size: ${inventoryActiveFilters.truckSize}`}
                  onDelete={() => removeInventoryFilter('truckSize')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {inventoryActiveFilters.truckYear && (
                <Chip
                  size="small"
                  label={`Truck year: ${inventoryActiveFilters.truckYear}`}
                  onDelete={() => removeInventoryFilter('truckYear')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {inventoryActiveFilters.lostReasonId && (
                <Chip
                  size="small"
                  label={`Lost reason: ${
                    inventoryFilterOptions.lostReasons.find(
                      (reason) => reason.id === inventoryActiveFilters.lostReasonId,
                    )?.label || inventoryActiveFilters.lostReasonId
                  }`}
                  onDelete={() => removeInventoryFilter('lostReasonId')}
                  color="primary"
                  variant="outlined"
                />
              )}
              <Button
                size="small"
                onClick={() => {
                  clearInventoryFilters();
                  clearSearch();
                }}
                sx={{ ml: 'auto' }}
              >
                Clear all
              </Button>
            </>
          ) : (
            <>
              {activeFilters.company && (
                <Chip
                  size="small"
                  label={`Company: ${activeFilters.company}`}
                  onDelete={() => removeFilter('company')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {activeFilters.year && (
                <Chip
                  size="small"
                  label={`Year: ${activeFilters.year}`}
                  onDelete={() => removeFilter('year')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {activeFilters.month && (
                <Chip
                  size="small"
                  label={`Month: ${months.find((m: { value: string }) => m.value === activeFilters.month)?.label}`}
                  onDelete={() => removeFilter('month')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {activeFilters.assignedRep && (
                <Chip
                  size="small"
                  label={`Sales Rep: ${getRepNameById(activeFilters.assignedRep)}`}
                  onDelete={() => removeFilter('assignedRep')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {activeFilters.truckType && (
                <Chip
                  size="small"
                  label={`Type: ${activeFilters.truckType}`}
                  onDelete={() => removeFilter('truckType')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {activeFilters.truckMake && (
                <Chip
                  size="small"
                  label={`Make: ${activeFilters.truckMake}`}
                  onDelete={() => removeFilter('truckMake')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {activeFilters.truckModel && (
                <Chip
                  size="small"
                  label={`Model: ${activeFilters.truckModel}`}
                  onDelete={() => removeFilter('truckModel')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {activeFilters.truckSize && (
                <Chip
                  size="small"
                  label={`Size: ${activeFilters.truckSize}`}
                  onDelete={() => removeFilter('truckSize')}
                  color="primary"
                  variant="outlined"
                />
              )}
              {activeFilters.truckYear && (
                <Chip
                  size="small"
                  label={`Truck Year: ${activeFilters.truckYear}`}
                  onDelete={() => removeFilter('truckYear')}
                  color="primary"
                  variant="outlined"
                />
              )}
              <Button
                size="small"
                onClick={() => {
                  clearFilters();
                  clearSearch();
                }}
                sx={{ ml: 'auto' }}
              >
                Clear all
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Out-of-scope leads alert */}
      {pipeline === 'sales' && outOfScopeLeadMatches.length > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2, borderRadius: 3 }}
        >
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Matching lead{outOfScopeLeadMatches.length !== 1 ? 's' : ''} found outside your assignment:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, listStyleType: 'disc' }}>
            {outOfScopeLeadMatches.map((lead: OutOfScopeLeadMatch) => {
              const metaLine = buildOutOfScopeMetaLine(lead);
              return (
                <Box key={lead.id} component="li" sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                  <Stack spacing={0.25}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {lead.customerName || 'Unknown name'} · {lead.company?.trim() || 'No company'} ·{' '}
                      {lead.assignedRepName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div">
                      {buildOutOfScopeSecondaryLine(lead)}
                    </Typography>
                    {metaLine ? (
                      <Typography variant="caption" color="text.secondary" component="div">
                        {metaLine}
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Alert>
      )}

      {/* Kanban on desktop; flat cards on phone for sales (no drag columns). */}
      {pipeline === 'inventory' ? (
        <InventoryLeadsBoard
          searchQuery={searchQuery || ''}
          showHiddenLeads={showHiddenLeads}
          activeFilters={inventoryActiveFilters}
          hiddenColumnIds={inventoryHiddenColumnIds}
          openColumnDialogSignal={inventoryColumnSignal}
          onStatsChange={handleInventoryStatsChange}
        />
      ) : isMobile ? (
        mobileSalesTotal === 0 ? (
          <MobileRecordList isEmpty empty='No leads.'>{null}</MobileRecordList>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {mobileSalesGroups.map((group) => (
              <Box key={group.columnId}>
                <Box
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    bgcolor: 'background.default',
                    py: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant='caption'
                    sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    {group.name}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {group.leads.length}
                  </Typography>
                </Box>
                <MobileRecordList>
                  {group.leads.map((lead) => {
                    const repName = lead.assignedRepId ? getRepNameById(lead.assignedRepId) : ''
                    return (
                      <MobileRecordCard
                        key={lead.id}
                        title={lead.customerName || 'Unknown name'}
                        meta={lead.company?.trim() || 'No company'}
                        detail={repName ? `Rep · ${repName}` : undefined}
                        status={{ tone: leadStatusTone(group.columnId), label: group.name }}
                        chips={collectLeadWants(lead)}
                        onClick={() => handleViewDetails(lead)}
                      />
                    )
                  })}
                </MobileRecordList>
              </Box>
            ))}
          </Box>
        )
      ) : (
      <DragDropContext onDragEnd={onDragEnd}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            overflowX: 'auto',
            overflowY: 'hidden',
            pb: 2,
            gap: 2,
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': { height: '8px' },
            '&::-webkit-scrollbar-track': {
              backgroundColor: alpha(theme.palette.text.primary, 0.05),
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.text.primary, 0.2),
              borderRadius: '4px',
              '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.3) },
            },
            minHeight: '700px',
          }}
        >
          {visibleColumnOrder.map((columnId: string) => {
            const column = state.columns[columnId];
            const isDefaultColumn = defaultColumns.includes(columnId);

            return (
              <Box
                key={columnId}
                sx={{
                  width: { xs: 250, sm: 280, md: 300 },
                  minWidth: { xs: 250, sm: 280, md: 300 },
                  position: 'relative',
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <LeadList
                  title={column.name}
                  leads={column.leads}
                  status={columnId as LeadStatus}
                  color={column.color}
                  onLeadSelect={handleLeadSelect}
                  onViewDetails={handleViewDetails}
                  onViewSuggestedTrucks={handleViewSuggestedTrucks}
                  onDeleteColumn={!isDefaultColumn ? handleDeleteColumn : undefined}
                  onDeleteLead={handleDeleteLead}
                  isDefaultColumn={isDefaultColumn}
                  columnName={column.name}
                />
              </Box>
            );
          })}
        </Box>
      </DragDropContext>
      )}

      <LeadFormDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        selectedLead={selectedLead}
        isCreating={isCreatingLead}
        initialClient={initialClientForLead}
      />

      {selectedLead && (
        <LeadDetailDialog
          open={isDetailDialogOpen}
          onClose={handleCloseDetailDialog}
          lead={selectedLead}
          onEdit={handleEditFromDetails}
          columnName={selectedLead.status ? state.columns[selectedLead.status]?.name : undefined}
          columnColor={selectedLead.status ? state.columns[selectedLead.status]?.color : undefined}
        />
      )}

      <Dialog open={isColumnDialogOpen} onClose={() => setIsColumnDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Column</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Column Title"
              fullWidth
              value={newColumnTitle}
              onChange={(e) => setNewColumnTitle(e.target.value)}
              margin="normal"
              required
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Position</InputLabel>
              <Select
                value={newColumnPosition}
                onChange={(e) => setNewColumnPosition(Number(e.target.value))}
                label="Position"
              >
                {state.columnOrder.map((_: string, index: number) => (
                  <MenuItem key={index} value={index}>
                    {index === 0 ? 'First' : `After ${state.columns[state.columnOrder[index - 1]].name}`}
                  </MenuItem>
                ))}
                <MenuItem value={state.columnOrder.length}>Last</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsColumnDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateColumn} variant="contained" disabled={!newColumnTitle.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDeleteColumnDialogOpen}
        onClose={() => setIsDeleteColumnDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Column</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete the column "
              {columnToDelete ? state.columns[columnToDelete]?.name : ''}"?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              All leads in this column will be moved to another column.
            </Typography>
            <FormControl fullWidth margin="normal">
              <InputLabel>Move leads to</InputLabel>
              <Select
                value={targetColumnForLeads}
                onChange={(e) => setTargetColumnForLeads(e.target.value)}
                label="Move leads to"
              >
                {state.columnOrder
                  .filter((id: string) => id !== columnToDelete)
                  .map((columnId: string) => (
                    <MenuItem key={columnId} value={columnId}>
                      {state.columns[columnId].name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteColumnDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteColumn} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isInventoryFilterDialogOpen}
        onClose={() => setIsInventoryFilterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Filter Inventory Leads</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Show columns
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
              {inventoryColumnOptions.map((column) => (
                <FormControlLabel
                  key={column.id}
                  control={
                    <Checkbox
                      checked={!inventoryHiddenColumnIds.includes(column.id)}
                      onChange={() => toggleInventoryColumnVisibility(column.id)}
                      size="small"
                    />
                  }
                  label={column.name}
                />
              ))}
            </Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 1 }}>
              Filter by
            </Typography>
            <FormControl fullWidth margin="normal">
              <Select
                value={invFilterCompanyId}
                onChange={(e) => setInvFilterCompanyId(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">All Companies</MenuItem>
                {inventoryFilterOptions.companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <Select
                value={invFilterAssignedRep}
                onChange={(e) => setInvFilterAssignedRep(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">All Inventory Reps</MenuItem>
                {inventoryReps.map((rep) => (
                  <MenuItem key={String(rep.id)} value={String(rep.id)}>
                    {getUserDisplayName(rep)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Year</InputLabel>
              <Select
                value={invFilterYear}
                onChange={(e) => {
                  setInvFilterYear(e.target.value);
                  if (!e.target.value) setInvFilterMonth('');
                }}
                label="Year"
              >
                <MenuItem value="">All Years</MenuItem>
                {INVENTORY_YEARS.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Month</InputLabel>
              <Select
                value={invFilterMonth}
                onChange={(e) => setInvFilterMonth(e.target.value)}
                label="Month"
                disabled={!invFilterYear}
              >
                <MenuItem value="">All Months</MenuItem>
                {INVENTORY_MONTHS.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Truck Type</InputLabel>
              <Select
                value={invFilterTruckType}
                onChange={(e) => setInvFilterTruckType(e.target.value)}
                label="Truck Type"
              >
                <MenuItem value="">All Truck Types</MenuItem>
                {inventoryFilterOptions.truckTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Truck Make</InputLabel>
              <Select
                value={invFilterTruckMake}
                onChange={(e) => setInvFilterTruckMake(e.target.value)}
                label="Truck Make"
              >
                <MenuItem value="">All Makes</MenuItem>
                {inventoryFilterOptions.truckMakes.map((make) => (
                  <MenuItem key={make} value={make}>
                    {make}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Truck Model</InputLabel>
              <Select
                value={invFilterTruckModel}
                onChange={(e) => setInvFilterTruckModel(e.target.value)}
                label="Truck Model"
              >
                <MenuItem value="">All Models</MenuItem>
                {inventoryFilterOptions.truckModels.map((model) => (
                  <MenuItem key={model} value={model}>
                    {model}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Truck Size</InputLabel>
              <Select
                value={invFilterTruckSize}
                onChange={(e) => setInvFilterTruckSize(e.target.value)}
                label="Truck Size"
              >
                <MenuItem value="">All Sizes</MenuItem>
                {inventoryFilterOptions.truckSizes.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Truck Year</InputLabel>
              <Select
                value={invFilterTruckYear}
                onChange={(e) => setInvFilterTruckYear(e.target.value)}
                label="Truck Year"
              >
                <MenuItem value="">All Years</MenuItem>
                {inventoryFilterOptions.truckYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Lost Reason</InputLabel>
              <Select
                value={invFilterLostReasonId}
                onChange={(e) => setInvFilterLostReasonId(e.target.value)}
                label="Lost Reason"
              >
                <MenuItem value="">All Lost Reasons</MenuItem>
                {inventoryFilterOptions.lostReasons.map((reason) => (
                  <MenuItem key={reason.id} value={reason.id}>
                    {reason.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearInventoryFilters}>Clear Filters</Button>
          <Button onClick={() => setIsInventoryFilterDialogOpen(false)}>Cancel</Button>
          <Button onClick={applyInventoryFilters} variant="contained">
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isFilterDialogOpen} onClose={() => setIsFilterDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Filter Sales Leads</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Show columns
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
              {state.columnOrder.map((columnId: string) => {
                const column = state.columns[columnId];
                if (!column) return null;
                return (
                  <FormControlLabel
                    key={columnId}
                    control={
                      <Checkbox
                        checked={!hiddenColumnIds.includes(columnId)}
                        onChange={() => toggleColumnVisibility(columnId)}
                        size="small"
                      />
                    }
                    label={column.name}
                  />
                );
              })}
            </Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 1 }}>
              Filter by
            </Typography>
            <FormControl fullWidth margin="normal">
              <Select value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)} displayEmpty>
                <MenuItem value="">All Companies</MenuItem>
                {availableCompanies.map((company: string) => (
                  <MenuItem key={company} value={company}>
                    {company}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <Select
                value={filterAssignedRep}
                onChange={(e) => setFilterAssignedRep(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">All Representatives</MenuItem>
                {salesReps.map((rep) => (
                  <MenuItem key={rep.id} value={rep.id}>
                    {rep.first_name} {rep.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Year</InputLabel>
              <Select value={filterYear} onChange={handleYearChange} label="Year">
                <MenuItem value="">All Years</MenuItem>
                {years.map((year: string) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Month</InputLabel>
              <Select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} label="Month">
                <MenuItem value="">All Months</MenuItem>
                {months.map((month: { value: string; label: string }) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Truck Type</InputLabel>
              <Select
                value={filterTruckType}
                onChange={(e) => setFilterTruckType(e.target.value)}
                label="Truck Type"
              >
                <MenuItem value="">All Truck Types</MenuItem>
                {availableTruckTypes.map((type: string) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Truck Make</InputLabel>
              <Select
                value={filterTruckMake}
                onChange={(e) => setFilterTruckMake(e.target.value)}
                label="Truck Make"
              >
                <MenuItem value="">All Makes</MenuItem>
                {availableTruckMakes.map((make: string) => (
                  <MenuItem key={make} value={make}>
                    {make}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Truck Model</InputLabel>
              <Select
                value={filterTruckModel}
                onChange={(e) => setFilterTruckModel(e.target.value)}
                label="Truck Model"
                disabled={availableTruckModels.length === 0}
              >
                <MenuItem value="">All Models</MenuItem>
                {availableTruckModels.map((model: string) => (
                  <MenuItem key={model} value={model}>
                    {model}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Truck Size</InputLabel>
              <Select
                value={filterTruckSize}
                onChange={(e) => setFilterTruckSize(e.target.value)}
                label="Truck Size"
                disabled={availableTruckSizes.length === 0}
              >
                <MenuItem value="">All Sizes</MenuItem>
                {availableTruckSizes.map((size: string) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Truck Year</InputLabel>
              <Select
                value={filterTruckYear}
                onChange={(e) => setFilterTruckYear(e.target.value)}
                label="Truck Year"
              >
                <MenuItem value="">All Years</MenuItem>
                {availableTruckYears.map((year: string) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearFilters}>Clear Filters</Button>
          <Button onClick={() => setIsFilterDialogOpen(false)}>Cancel</Button>
          <Button onClick={applyFilters} variant="contained">
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDeleteLeadDialogOpen}
        onClose={() => setIsDeleteLeadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Lead</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete this lead?
            </Typography>
            {leadToDelete && (
              <Typography variant="body2" color="text.secondary">
                Lead: {leadToDelete.company} - {leadToDelete.customerName}
              </Typography>
            )}
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              This action cannot be undone.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteLeadDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteLead} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isMissingDropoffDialogOpen}
        onClose={() => setIsMissingDropoffDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Truck Dropoff Required</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body1">
              Each linked truck must have a complete dropoff address (street, city, state, and ZIP)
              from the company&apos;s dropoff locations before this lead can be closed.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsMissingDropoffDialogOpen(false)} variant="contained" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadsTab;
